import { createTrackingUtils } from "../index";
import type { SBState, SBLocation } from "@bookingcom/flights-searchbox";
import useProfiler from "../../../hooks/useProfiler";
import { isEqual } from "lodash";
import { useCallback } from "react";
import {
  LOCATION_IATA_CODE_AND_TYPE_SEPARATOR,
  PARAM_LIST_SEPARATOR,
  MULTISTOP_IATA_LIST_SEPARATOR
} from "@flights/constants";

const utils = createTrackingUtils({
  name: "flights_web_cat_returning_user_prefill_search",
  stages: {
    users_with_prefilled_search: 1,
    desktop: 2,
    mdot: 3,
    book_window_short: 4,
    book_window_medium: 5,
    book_window_long: 6,
    one_way: 7,
    round_trip: 8,
    multi_city: 9
  },
  defaultStage: 1,
  goals: {
    origin_changed: 1,
    destination_changed: 2,
    dates_changed: 3,
    anything_changed: 4
  },
  goalsWithValue: ["flights_cat_index_time_from_load_to_search_in_sec"]
});

export const useTrackReturningUserPrefill = () => {
  const { isDesktop, isMobile, isLastMinute, isLateBird, isEarlyBird, isOneWay, isRountrip, isMultiStop } =
    useProfiler();

  return useCallback(
    (prefilledSearchState: SBState, currentSearchState: SBState) => {
      const { stages } = utils;

      isDesktop() && stages.desktop();
      isMobile() && stages.mdot();
      isLastMinute() && stages.book_window_short();
      isLateBird() && stages.book_window_medium();
      isEarlyBird() && stages.book_window_long();
      isOneWay() && stages.one_way();
      isRountrip() && stages.round_trip();
      isMultiStop() && stages.multi_city();

      const originChanged = getFromCodes(prefilledSearchState) !== getFromCodes(currentSearchState);
      if (originChanged) {
        utils.goals.origin_changed();
      }

      const destinationChanged = getToCodes(prefilledSearchState) !== getToCodes(currentSearchState);
      if (destinationChanged) {
        utils.goals.destination_changed();
      }

      const datesChanged = getDates(prefilledSearchState) !== getDates(currentSearchState);
      if (datesChanged) {
        utils.goals.dates_changed();
      }

      const otherFieldsChanged = (["adults", "children", "cabinClass", "type", "isDirect"] as const).filter(
        (field) => !isEqual(prefilledSearchState[field], currentSearchState[field])
      );

      const anyFieldChanged = originChanged || destinationChanged || datesChanged || otherFieldsChanged.length > 0;
      if (anyFieldChanged) {
        utils.goals.anything_changed();
      }
    },
    [isDesktop, isMobile, isLastMinute, isLateBird, isEarlyBird, isOneWay, isRountrip, isMultiStop]
  );
};

export const getLocationCodes = (locations: SBLocation[]) =>
  locations
    .map(({ code, type }) => [code, type].filter(Boolean).join(LOCATION_IATA_CODE_AND_TYPE_SEPARATOR))
    .join(PARAM_LIST_SEPARATOR);

export const getFromCodes = (state: SBState) =>
  state.searchSegments.map((segment) => getLocationCodes(segment.from)).join(MULTISTOP_IATA_LIST_SEPARATOR);

export const getToCodes = (state: SBState) =>
  state.searchSegments.map((segment) => getLocationCodes(segment.to)).join(MULTISTOP_IATA_LIST_SEPARATOR);

export const getDates = (state: SBState) =>
  state.searchSegments
    .map((segment) => `${segment.departureDate || ""}-${segment.returnDate || ""}`)
    .join(MULTISTOP_IATA_LIST_SEPARATOR);

export default { ...utils, useTrackReturningUserPrefill };
